import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from './api';

/*
 * The Stories & Tech Step 3 admin calls, typed from the generated client (S3-INT regenerated it from the integrated API and replaced
 * S3-ADM's typed stubs). A 404 still shows "Not available yet" (an older API), and the pages keep working offline from the draft.
 *
 * | Function | Endpoint | Owner |
 * |---|---|---|
 * | `previewArenaTemplate` | `POST /admin/arena/preview` | ARS |
 * | `previewOverwatch` | `POST /admin/overwatch/preview` | OWS |
 * | `overwatchAttempts` | `GET /admin/overwatch/attempts` | OWS |
 * | `overwatchAttempt` | `GET /admin/overwatch/attempts/{id}` | OWS |
 */

/** A 404 from an endpoint the API doesn't have (an older deploy). */
export const isNotAvailable = (e: unknown) => /\b404\b|not found/i.test(String(e));

// ----- arena template preview (S3-ARS, ArenaEndpoints.Preview): ArenaGenerator over the draft template for a seed ----------------------

/** `AdminArenaPreviewRequest`: a draft template (or the id of one in the current content), a vehicle, a horde size and a seed. */
export type ArenaPreviewRequest = Schemas['AdminArenaPreviewRequest'];
/** `ArenaLayout` as the server generates it (without the nav grid). */
export type ArenaPreviewLayout = Schemas['ArenaLayout'];
/** `AdminArenaPreviewResponse`: the layout, the shortest legal run line, each vehicle's duration floor, the C# validator's errors. */
export type ArenaPreviewResponse = Schemas['AdminArenaPreviewResponse'];

export const previewArenaTemplate = async (client: ApiClient, body: ArenaPreviewRequest) =>
  ok(await client.POST('/admin/arena/preview', { body }));

// ----- overwatch plan preview (S3-OWS, OverwatchEndpoints.Preview): the mission played from a seed with no shots, sampled ------------

/** `OverwatchPreviewRequest`: a mission, a seed, the draft `overwatch` section (null = the current content's), the sampling step. */
export type OverwatchPreviewRequest = Schemas['OverwatchPreviewRequest'];
/** `OverwatchPreviewFigure`: the roster entry and its sampled path (`t` in ms, metres on the plan). */
export type OverwatchPreviewFigure = Schemas['OverwatchPreviewFigure'];
/** `OverwatchPreviewResponse`. */
export type OverwatchPreviewResponse = Schemas['OverwatchPreviewResponse'];

export const previewOverwatch = async (client: ApiClient, body: OverwatchPreviewRequest) =>
  ok(await client.POST('/admin/overwatch/preview', { body }));

// ----- overwatch attempts (S3-OWS): the list, and one attempt with its submitted log and the server's verdict -------------------------

export type OverwatchAttemptRow = Schemas['OverwatchAttemptRow'];
export type OverwatchAttemptsPage = Schemas['OverwatchAttemptsPage'];
/** `OverwatchAttemptDetail`: the row, the signed block, the submitted result (shot log, aim track), the verdict and notes. */
export type OverwatchAttemptDetail = Schemas['OverwatchAttemptDetail'];

export const overwatchAttempts = async (
  client: ApiClient,
  query: { playerId?: string; missionId?: string; status?: string; offset?: number; take?: number },
) => ok(await client.GET('/admin/overwatch/attempts', { params: { query } }));

export const overwatchAttempt = async (client: ApiClient, id: string) =>
  ok(await client.GET('/admin/overwatch/attempts/{id}', { params: { path: { id } } }));
